// src/views/Operaciones/LiquidacionClientes/ListarLiquidacionClientes.jsx
import React, { useEffect, useState } from "react";
import { FaEye, FaEdit } from "react-icons/fa";

import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";

import FiltroView from "./FiltroEjecucionDeLiquidaciones";
import EditarView from "./EditarEjecucionDeLiquidacion";
import VisualizarView from "./VerEjecucionDeLiquidaciones";

// ============================
// CONFIG
// ============================
const DEFAULT_PAGE_SIZE = 30;

// ============================
// COMPONENTE
// ============================
export default function ListarEjecucionDeLiquidaciones() {
  // Formateador de números enteros
  const fmtInt = new Intl.NumberFormat("es-PY", {
    maximumFractionDigits: 0,
  });

  // ============================
  // ESTADO LISTA
  // ============================
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);

  // ============================
  // FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  const limpiarSeleccion = () => {
    const empty = {};
    setFiltros(empty);
    setPaginaActual(1);
    fetchConsulta(1, porPagina, empty);
  };

  // ============================
  // MODALES
  // ============================
  const [showEdit, setShowEdit] = useState(false);
  const [idEdit, setIdEdit] = useState(null);
  useEscClose(() => setShowEdit(false), showEdit);

  const [showView, setShowView] = useState(false);
  const [idView, setIdView] = useState(null);
  useEscClose(() => setShowView(false), showView);

  // ============================
  // FETCH
  // ============================
  const fetchConsulta = async (
    page = paginaActual,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page - 1));
      params.set("size", String(size));

      Object.entries(filtrosAplicados || {}).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        if (typeof v === "object") return; // evitar objetos
        const s = String(v).trim();
        if (s === "") return;
        params.set(k, s);
      });

      const url = `${apiRoutes.liquidacionEjecucion.list}?${params.toString()}`;
      const data = await appFetchJson(url);

      setItems(data.items || []);
      setTotalBackend(data.totalElements || 0);
    } catch (e) {
      console.error("Error cargando ejecución de liquidaciones", e);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  // ============================
  // INIT
  // ============================
  useEffect(() => {
    fetchConsulta(1, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================
  // PAGINACIÓN
  // ============================
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));

  const cambiarPagina = (num) => {
    const pagina = Math.min(Math.max(num, 1), totalPaginas);
    setPaginaActual(pagina);
    fetchConsulta(pagina, porPagina);
  };

  // ============================
  // DEFINICIÓN DE COLUMNAS (DynamicTable)
  // ============================
  const columns = [
    {
      header: "Cód. Liqui",
      accessor: "id",
      type: "code",
      width: "5%",
      cellClass: "font-mono",
    },
    {
      header: "Estado",
      accessor: "estadoNombre",
      type: "text",
      width: "8%",
    },
    {
      header: "Liquidación",
      accessor: "descripcion",
      type: "text",
      width: "12%",
    },
    {
      header: "Fecha Inicio",
      type: "center",
      width: "8%",
      render: (row) => row.dataFechaini ?? "—",
    },
    {
      header: "Fecha Fin",
      type: "center",
      width: "8%",
      render: (row) => row.dataFechafin ?? "—",
    },
    {
      header: "Remitos",
      type: "center",
      width: "7%",
      render: (row) => fmtInt.format(row.qnRemitosProc ?? 0),
    },
    {
      header: "Desc. Libre",
      type: "center",
      width: "10%",
      render: (row) => fmtInt.format(row.valorTotalDescLibe ?? 0),
    },
    {
      header: "Facturas",
      type: "center",
      width: "7%",
      render: (row) => fmtInt.format(row.qnFactura ?? 0),
    },
    {
      header: "NC",
      type: "center",
      width: "7%",
      render: (row) => fmtInt.format(row.qnNotaCredito ?? 0),
    },
    {
      header: "Sin Fact.",
      type: "center",
      width: "10%",
      render: (row) => fmtInt.format(row.qnClieSinFacturar ?? 0),
    },
    {
      header: "Acción",
      type: "action",
      width: "8%",
      render: (row) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="icon-btn"
            title="Ver"
            onClick={() => {
              setIdView(row.id);
              setShowView(true);
            }}
          >
            <FaEye />
          </button>
          <button
            className="icon-btn"
            title="Editar"
            onClick={() => {
              setIdEdit(row.id);
              setShowEdit(true);
            }}
          >
            <FaEdit />
          </button>
        </div>
      ),
    },
  ];

  // ============================
  // RENDER
  // ============================
  return (
    <div className="page">
      <ListHeader
        title="Ejecución de Liquidaciones"
        onFilterClick={() => setMostrarFiltros(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchConsulta(1, size, filtros);
        }}
      />

      {/* FILTROS */}
      {mostrarFiltros && (
        <FiltroView
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchConsulta(1, porPagina, nuevosFiltros);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* EDITAR */}
      {showEdit && (
        <EditarView
          id={idEdit}
          onClose={() => setShowEdit(false)}
          onSaved={() => fetchConsulta(paginaActual, porPagina, filtros)}
        />
      )}

      {/* VER */}
      {showView && (
        <VisualizarView id={idView} onClose={() => setShowView(false)} />
      )}

      {/* INDICADOR DE CARGA */}
      {cargando && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Cargando datos...
        </div>
      )}

      {/* TABLA RESPONSIVA */}
      {!cargando && (
        <DynamicTable
          size="L" // Breakpoint en 1210px para manejar 11 columnas
          data={items}
          columns={columns}
        />
      )}

      {/* PAGINADOR */}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />
    </div>
  );
}